'use client'

import { Children, type CSSProperties, type ReactNode } from 'react'

import { TrackedProductCard } from '@/app/components/shared/TrackedProductCard'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { NOT_SET } from '@/app/lib/analytics/constants'
import { withIndex } from '@/app/lib/analytics/mapProductToAnalyticsItem'
import type { ContextualProductListAnalytics } from '@/app/lib/analytics/types'
import { useProductListTracking } from '@/app/lib/analytics/useProductListTracking'

interface TrackedProductGridProps {
  readonly className?: string
  /** Variables CSS dinámicas del grid (p. ej. `--highlight-columns`). */
  readonly style?: CSSProperties
  readonly analytics: ContextualProductListAnalytics
  /**
   * Posición absoluta (0-based) de la primera card de este grid dentro del
   * listado completo del módulo. Solo hace falta cuando un mismo módulo reparte
   * sus cards en varios grids (p. ej. un grid por mes): el `items[].index` de
   * cada `view_item_list`/`select_item` sigue siendo absoluto.
   */
  readonly indexOffset?: number
  /** Las cards ya renderizadas por el Server Component padre, en orden. */
  readonly children: ReactNode
}

/**
 * Envoltorio cliente genérico de un grid de cards de producto: emite el
 * `view_item_list` (impresiones agrupadas al entrar cada card en viewport,
 * threshold 0.5) y el `select_item` de la card clicada.
 *
 * Recibe las cards como `children`, así `VerticalCard`/`HorizontalCard` siguen
 * siendo Server Components y solo este envoltorio entra en el bundle de
 * cliente. Mismo patrón que `TrackedOtherProvinceGrid`, pero sin atarse a una
 * franja concreta: el `item_list_id` se deriva del `page_type` del contexto
 * (`<page_type>_<module_type>`), así que el módulo que lo usa no conoce la
 * página. Fuera de `PageAnalyticsProvider` (tests, páginas sin instrumentar)
 * no emite nada y no falla.
 */
export function TrackedProductGrid({
  className,
  style,
  analytics,
  indexOffset = 0,
  children,
}: TrackedProductGridProps) {
  const pageAnalytics = useOptionalPageAnalytics()
  const { trackImpression, trackSelect, trackPurchase } = useProductListTracking(
    {
      itemListId: `${pageAnalytics?.context.page_type ?? NOT_SET}_${analytics.moduleType}`,
      itemListName: analytics.itemListName,
      moduleName: analytics.moduleName,
      moduleType: analytics.moduleType,
      modulePosition: analytics.modulePosition,
    },
    {
      trackViewItemList: pageAnalytics?.trackViewItemList,
      trackSelectItem: pageAnalytics?.trackSelectItem,
      trackAddToCart: pageAnalytics?.trackAddToCart,
    },
  )

  return (
    <div className={className} style={style}>
      {Children.map(children, (child, position) => {
        const item = analytics.items[position]
        if (!item) return child

        const index = indexOffset + position
        return (
          <TrackedProductCard
            index={index}
            onImpression={() => trackImpression(`${index}:${item.item_id}`, withIndex(item, index))}
            onSelect={() => trackSelect(withIndex(item, index))}
            onPurchase={(href) => trackPurchase(withIndex(item, index), href)}
          >
            {child}
          </TrackedProductCard>
        )
      })}
    </div>
  )
}
